import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateFixedAssetInput,
  DisposeFixedAssetInput,
  FixedAsset,
  FixedAssetWithSchedule,
  RunDepreciationResult,
  UpdateFixedAssetInput,
} from '@accounting/shared';
import { api } from '../lib/api';

export function useFixedAssets(companyId: string | null) {
  return useQuery({
    queryKey: ['fixed-assets', companyId ?? 'none'],
    enabled: companyId !== null,
    queryFn: () =>
      api.get<{ fixedAssets: FixedAsset[] }>(`/companies/${companyId}/fixed-assets`).then((r) => r.fixedAssets),
  });
}

export function useFixedAsset(companyId: string | null, id: string) {
  return useQuery({
    queryKey: ['fixed-asset', companyId ?? 'none', id],
    enabled: companyId !== null && !!id,
    queryFn: () =>
      api.get<{ fixedAsset: FixedAssetWithSchedule }>(`/companies/${companyId}/fixed-assets/${id}`).then((r) => r.fixedAsset),
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>, companyId: string | null) {
  const c = companyId ?? 'none';
  qc.invalidateQueries({ queryKey: ['fixed-assets', c] });
  qc.invalidateQueries({ queryKey: ['fixed-asset', c] });
  qc.invalidateQueries({ queryKey: ['journal-entries', c] });
  qc.invalidateQueries({ queryKey: ['accounts-balances', c] });
  qc.invalidateQueries({ queryKey: ['report'] });
}

export function useCreateFixedAsset(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateFixedAssetInput) => {
      if (!companyId) throw new Error('No active company');
      return api.post<{ fixedAsset: FixedAssetWithSchedule }>(`/companies/${companyId}/fixed-assets`, input).then((r) => r.fixedAsset);
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}

export function useUpdateFixedAsset(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateFixedAssetInput }) => {
      if (!companyId) throw new Error('No active company');
      return api
        .patch<{ fixedAsset: FixedAssetWithSchedule }>(`/companies/${companyId}/fixed-assets/${id}`, input)
        .then((r) => r.fixedAsset);
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}

export function useRunDepreciation(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, throughDate }: { id: string; throughDate: string }) => {
      if (!companyId) throw new Error('No active company');
      return api.post<{ result: RunDepreciationResult }>(`/companies/${companyId}/fixed-assets/${id}/depreciate`, { throughDate }).then((r) => r.result);
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}

export function useDisposeFixedAsset(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: DisposeFixedAssetInput }) => {
      if (!companyId) throw new Error('No active company');
      return api.post<{ fixedAsset: FixedAsset }>(`/companies/${companyId}/fixed-assets/${id}/dispose`, input).then((r) => r.fixedAsset);
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}
